import React, { useState } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Label } from './label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { MultiSelect } from './multi-select';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

const UNIT_OPTIONS = ['Meters', 'Bales', 'Thans', 'Kg', 'Pieces'];

export const QuickAddSupplier = ({ open, onOpenChange, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', city: '', phone: '' });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await api.post('/suppliers', form);
      toast.success('Supplier created!');
      onCreated(res.data);
      onOpenChange(false);
      setForm({ name: '', city: '', phone: '' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add supplier'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Quick Add Supplier</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Supplier Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus /></div>
          <div><Label>City *</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Creating...' : 'Create Supplier'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const QuickAddQuality = ({ open, onOpenChange, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ quality_name: '', default_unit: 'Meters', unit_type: 'Meters' });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await api.post('/qualities', form);
      toast.success('Quality created!');
      onCreated(res.data);
      onOpenChange(false);
      setForm({ quality_name: '', default_unit: 'Meters', unit_type: 'Meters' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add quality'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Quick Add Quality</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Quality Name *</Label><Input value={form.quality_name} onChange={(e) => setForm({ ...form, quality_name: e.target.value })} required autoFocus /></div>
          <div><Label>Default Unit *</Label>
            <Select value={form.default_unit} onValueChange={(v) => setForm({ ...form, default_unit: v, unit_type: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{UNIT_OPTIONS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Creating...' : 'Create Quality'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const QuickAddSubQuality = ({ open, onOpenChange, onCreated, qualities, defaultQualityId }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ subquality_name: '', quality_ids: defaultQualityId ? [defaultQualityId] : [] });
  // Reset when dialog opens, defaulting to current parent if provided (improves quick-add flow from order forms)
  React.useEffect(() => {
    if (open) {
      setForm({ subquality_name: '', quality_ids: defaultQualityId ? [defaultQualityId] : [] });
    }
  }, [open, defaultQualityId]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.quality_ids || form.quality_ids.length === 0) {
      toast.error('Please select at least one parent quality');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/subqualities', {
        subquality_name: form.subquality_name,
        quality_ids: form.quality_ids,
      });
      toast.success('Finish Quality created!');
      onCreated(res.data);
      onOpenChange(false);
      setForm({ subquality_name: '', quality_ids: [] });
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add finish quality'); }
    finally { setLoading(false); }
  };
  const qualityOptions = qualities.map(q => ({ value: q.quality_id, label: q.quality_name }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Quick Add Finish Quality</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Parent Qualities * <span className="text-xs text-slate-500 font-normal">(one or more)</span></Label>
            <MultiSelect
              value={form.quality_ids}
              onChange={(next) => setForm({ ...form, quality_ids: next })}
              options={qualityOptions}
              placeholder="Select parent qualities"
              testId="quick-add-subquality-parents"
            />
          </div>
          <div><Label>Finish Quality Name *</Label><Input value={form.subquality_name} onChange={(e) => setForm({ ...form, subquality_name: e.target.value })} required /></div>
          <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Creating...' : 'Create Finish Quality'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const QuickAddTransport = ({ open, onOpenChange, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ transport_name: '', contact_person: '', phone: '' });
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await api.post('/transports', form);
      toast.success('Transport created!');
      onCreated(res.data);
      onOpenChange(false);
      setForm({ transport_name: '', contact_person: '', phone: '' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add transport'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Quick Add Transport</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><Label>Transport Name *</Label><Input value={form.transport_name} onChange={(e) => setForm({ ...form, transport_name: e.target.value })} required autoFocus /></div>
          <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">{loading ? 'Creating...' : 'Create Transport'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Dropdown with an inline "Add New" button at the bottom
 */
export const SelectWithQuickAdd = ({ value, onValueChange, placeholder, items, labelKey, valueKey, onAddClick, testId }) => {
  return (
    <div className="flex gap-2">
      <div className="flex-1">
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger data-testid={testId} className="h-11">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item[valueKey]} value={item[valueKey]}>
                {typeof labelKey === 'function' ? labelKey(item) : item[labelKey]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-11 w-11 shrink-0 border-dashed border-slate-300 hover:border-slate-500 hover:bg-slate-50"
        onClick={onAddClick}
        title="Add New"
        data-testid={testId ? `${testId}-add-new` : undefined}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
};
